import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  TrophyIcon,
  PlusIcon,
  CalendarIcon,
  UsersIcon,
  AwardIcon,
  ClockIcon,
  MapPinIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  PlayCircleIcon,
  PauseCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  TargetIcon,
  TrendingUpIcon,
  GlobeIcon,
  MapIcon,
  GraduationCapIcon,
  Building2Icon,
  FolderIcon,
  LayersIcon,
  LockIcon
} from 'lucide-react';
import api from '../../services/api';
import { authService } from '../../services/authService';
import toast from 'react-hot-toast';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getAcademyTranslation } from '../../services/academiesTranslationsService';
import { getDistrictTranslation } from '../../services/districtsTranslationsService';
import { getInstitutionTranslation } from '../../services/institutionsTranslationsService';
import { getDepartmentTranslation } from '../../services/departmentsTranslationsService';

// استخراج هيكل تنظيمي (معرفات) من عنصر organizationalAccess
const normalizeOrgScope = (access) => {
  if (!access || typeof access !== 'object') return null;
  const id = (key) => access[key] ?? access[key.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')];
  const countryId = id('countryId') ?? id('country_id');
  const regionId = id('regionId') ?? id('region_id');
  const academyId = id('academyId') ?? id('academy_id');
  const districtId = id('districtId') ?? id('district_id');
  const institutionId = id('institutionId') ?? id('institution_id');
  const departmentId = id('departmentId') ?? id('department_id');
  const scope = {
    countryId: countryId != null && countryId !== '' ? Number(countryId) : '',
    regionId: regionId != null && regionId !== '' ? Number(regionId) : '',
    academyId: academyId != null && academyId !== '' ? Number(academyId) : '',
    districtId: districtId != null && districtId !== '' ? Number(districtId) : '',
    institutionId: institutionId != null && institutionId !== '' ? Number(institutionId) : '',
    departmentId: departmentId != null && departmentId !== '' ? Number(departmentId) : ''
  };
  const hasAny = Object.values(scope).some((v) => v !== '' && v != null);
  return hasAny ? scope : null;
};

// استخراج أسماء الهيكل من organizationalAccess
const normalizeOrgScopeNames = (access) => {
  if (!access || typeof access !== 'object') return null;
  const n = (camel, snake) => access[camel] ?? access[snake] ?? '';
  return {
    countryName: n('countryName', 'country_name'),
    regionName: n('regionName', 'region_name'),
    academyName: n('academyName', 'academy_name'),
    districtName: n('districtName', 'district_name'),
    institutionName: n('institutionName', 'institution_name'),
    departmentName: n('departmentName', 'department_name')
  };
};

const CompetitionManagement = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();

  // State management
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  // Statistics state
  const [stats, setStats] = useState({
    totalCompetitions: 0,
    activeCompetitions: 0,
    upcomingCompetitions: 0,
    completedCompetitions: 0,
    totalParticipants: 0,
    averageParticipants: 0
  });

  // Add competition modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  
  // Status update modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  const [newCompetition, setNewCompetition] = useState({
    name: '',
    description: '',
    totalRounds: 5,
    flashAnzanMode: 'shared',
    targetLevelId: '',
    scope: {
      countryId: '',
      regionId: '',
      academyId: '',
      districtId: '',
      institutionId: '',
      departmentId: ''
    },
    schedule: {
      registrationStart: '',
      registrationEnd: '',
      competitionStart: '',
      competitionEnd: ''
    },
    settings: {
      maxParticipants: 500,
      allowLateRegistration: false,
      requireApproval: true,
      maxParticipantsPerDistrict: 50,
      maxParticipantsPerAcademy: 100,
      maxParticipantsPerRegion: 200,
      maxParticipantsPerCountry: 500
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Organizational data state
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingOrgData, setLoadingOrgData] = useState(false);
  const [learningLevels, setLearningLevels] = useState([]);
  const [learningLevelsLoading, setLearningLevelsLoading] = useState(false);

  // Lookup maps for quick access
  const [orgLookupMaps, setOrgLookupMaps] = useState({
    countries: {},
    regions: {},
    academies: {},
    districts: {},
    institutions: {},
    departments: {}
  });

  // نطاق الهيكل التنظيمي للمستخدم الحالي (لربط المسابقة به فقط)
  const [compScopeFromCurrentUser, setCompScopeFromCurrentUser] = useState(null);
  const [compScopeNamesFromCurrentUser, setCompScopeNamesFromCurrentUser] = useState(null);

  const levelToNameKey = {
    countryId: 'countryName',
    regionId: 'regionName',
    academyId: 'academyName',
    districtId: 'districtName',
    institutionId: 'institutionName',
    departmentId: 'departmentName'
  };
  const isCompScopeLevelFixed = (levelKey) => {
    const nameKey = levelToNameKey[levelKey];
    if (nameKey && compScopeNamesFromCurrentUser) {
      const name = compScopeNamesFromCurrentUser[nameKey];
      if (name != null && name !== '') return true;
    }
    return !!(compScopeFromCurrentUser && compScopeFromCurrentUser[levelKey] !== '' && compScopeFromCurrentUser[levelKey] != null);
  };

  // Load all organizational data for lookup
  const loadOrganizationalLookupMaps = async () => {
    try {
      const [countriesRes, regionsRes, academiesRes, districtsRes, institutionsRes, departmentsRes] = await Promise.all([
        api.get('/organization/countries?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/regions?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/academies?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/districts?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/institutions?page=1&limit=100').catch(() => ({ data: { success: false } })),
        api.get('/organization/departments?page=1&limit=100').catch(() => ({ data: { success: false } }))
      ]);

      const maps = {
        countries: {},
        regions: {},
        academies: {},
        districts: {},
        institutions: {},
        departments: {}
      };

      if (countriesRes.data.success) {
        countriesRes.data.data.countries?.forEach(item => {
          maps.countries[item.id] = {
            name: item.name,
            translatedName: getCountryTranslation(item.name, currentLanguage)
          };
        });
      }

      if (regionsRes.data.success) {
        regionsRes.data.data.regions?.forEach(item => {
          maps.regions[item.id] = {
            name: item.name,
            translatedName: getRegionTranslation(item.name, currentLanguage)
          };
        });
      }

      if (academiesRes.data.success) {
        academiesRes.data.data.academies?.forEach(item => {
          maps.academies[item.id] = {
            name: item.name,
            translatedName: getAcademyTranslation(item.name, currentLanguage)
          };
        });
      }

      if (districtsRes.data.success) {
        districtsRes.data.data.districts?.forEach(item => {
          maps.districts[item.id] = {
            name: item.name,
            translatedName: getDistrictTranslation(item.name, currentLanguage)
          };
        });
      }

      if (institutionsRes.data.success) {
        institutionsRes.data.data.institutions?.forEach(item => {
          maps.institutions[item.id] = {
            name: item.name,
            translatedName: getInstitutionTranslation(item.name, currentLanguage)
          };
        });
      }

      if (departmentsRes.data.success) {
        departmentsRes.data.data.departments?.forEach(item => {
          maps.departments[item.id] = {
            name: item.name,
            translatedName: getDepartmentTranslation(item.name, currentLanguage)
          };
        });
      }

      setOrgLookupMaps(maps);
    } catch (error) {
      console.error('Error loading organizational lookup maps:', error);
    }
  };

  // Fetch competitions - API: /competitions?page=1&limit=10
  const fetchCompetitions = async (page = 1, itemsPerPage = limit) => {
    setLoading(true);
    try {
      const response = await api.get('/competitions', {
        params: { page, limit: itemsPerPage }
      });

      if (response.data.success) {
        const res = response.data.data;
        const competitionsData = Array.isArray(res) ? res : (res?.competitions || []);
        const pagination = res?.pagination || {};
        
        // استخدام lookup maps لتحديد أعمق نطاق
        const competitionsWithNames = competitionsData.map((comp) => {
          let scopeInfo = {
            deepestLevel: null,
            deepestLevelType: null,
            deepestLevelIcon: null
          };
          
          // تحديد أعمق مستوى محدد في النطاق
          // الترتيب: Department > Institution > District > Academy > Region > Country
          
          if (comp.scope?.departmentId && orgLookupMaps.departments[comp.scope.departmentId]) {
            scopeInfo.deepestLevel = orgLookupMaps.departments[comp.scope.departmentId].translatedName;
            scopeInfo.deepestLevelType = 'department';
            scopeInfo.deepestLevelIcon = FolderIcon;
          } else if (comp.scope?.institutionId && orgLookupMaps.institutions[comp.scope.institutionId]) {
            scopeInfo.deepestLevel = orgLookupMaps.institutions[comp.scope.institutionId].translatedName;
            scopeInfo.deepestLevelType = 'institution';
            scopeInfo.deepestLevelIcon = Building2Icon;
          } else if (comp.scope?.districtId && orgLookupMaps.districts[comp.scope.districtId]) {
            scopeInfo.deepestLevel = orgLookupMaps.districts[comp.scope.districtId].translatedName;
            scopeInfo.deepestLevelType = 'district';
            scopeInfo.deepestLevelIcon = MapPinIcon;
          } else if (comp.scope?.academyId && orgLookupMaps.academies[comp.scope.academyId]) {
            scopeInfo.deepestLevel = orgLookupMaps.academies[comp.scope.academyId].translatedName;
            scopeInfo.deepestLevelType = 'academy';
            scopeInfo.deepestLevelIcon = GraduationCapIcon;
          } else if (comp.scope?.regionId && orgLookupMaps.regions[comp.scope.regionId]) {
            scopeInfo.deepestLevel = orgLookupMaps.regions[comp.scope.regionId].translatedName;
            scopeInfo.deepestLevelType = 'region';
            scopeInfo.deepestLevelIcon = MapIcon;
          } else if (comp.scope?.countryId && orgLookupMaps.countries[comp.scope.countryId]) {
            scopeInfo.deepestLevel = orgLookupMaps.countries[comp.scope.countryId].translatedName;
            scopeInfo.deepestLevelType = 'country';
            scopeInfo.deepestLevelIcon = GlobeIcon;
          }
          
          return {
            ...comp,
            scopeInfo
          };
        });
        
        setCompetitions(competitionsWithNames);
        setTotal(pagination.total ?? competitionsWithNames.length);
        setTotalPages(pagination.pages ?? Math.ceil((pagination.total || competitionsWithNames.length) / (itemsPerPage || limit)));
        setCurrentPage(pagination.page ?? page);
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
      toast.error(t('competitions.fetchError') || 'Failed to fetch competitions', {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await api.get('/competitions/statistics');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // حساب الإحصائيات من البيانات المحلية إذا فشل جلب الإحصائيات
      if (competitions.length > 0) {
        const calculatedStats = {
          totalCompetitions: competitions.length,
          activeCompetitions: competitions.filter(c => c.status === 'active').length,
          upcomingCompetitions: competitions.filter(c => c.status === 'upcoming' || c.status === 'draft').length,
          completedCompetitions: competitions.filter(c => c.status === 'completed').length,
          totalParticipants: 0,
          averageParticipants: 0
        };
        setStats(calculatedStats);
      }
    }
  };

  // Fetch organizational data
  const fetchOrganizationalData = async () => {
    setLoadingOrgData(true);
    try {
      const countriesResponse = await api.get('/organization/countries?page=1&limit=100');
      if (countriesResponse.data.success) {
        // إضافة الترجمات للدول
        const countriesWithTranslations = countriesResponse.data.data.countries.map(country => ({
          ...country,
          translatedName: getCountryTranslation(country.name, currentLanguage)
        }));
        setCountries(countriesWithTranslations || []);
      }
    } catch (error) {
      console.error('Error fetching organizational data:', error);
      toast.error(t('competitions.orgDataFetchError') || 'Error fetching organizational data', {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setLoadingOrgData(false);
    }
  };

  // Fetch regions by country
  const fetchRegionsByCountry = async (countryId) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/countries/${countryId}/regions?page=1&limit=100`);
      if (response.data.success) {
        // إضافة الترجمات للجهات
        const regionsWithTranslations = response.data.data.regions.map(region => ({
          ...region,
          translatedName: getRegionTranslation(region.name, currentLanguage)
        }));
        setRegions(regionsWithTranslations || []);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  // Fetch academies by region
  const fetchAcademiesByRegion = async (regionId) => {
    if (!regionId) {
      setAcademies([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/regions/${regionId}/academies?page=1&limit=100`);
      if (response.data.success) {
        // إضافة الترجمات للأكاديميات
        const academiesWithTranslations = response.data.data.academies.map(academy => ({
          ...academy,
          translatedName: getAcademyTranslation(academy.name, currentLanguage)
        }));
        setAcademies(academiesWithTranslations || []);
      }
    } catch (error) {
      console.error('Error fetching academies:', error);
    }
  };

  // Fetch districts by academy
  const fetchDistrictsByAcademy = async (academyId) => {
    if (!academyId) {
      setDistricts([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/academies/${academyId}/districts?page=1&limit=100`);
      if (response.data.success) {
        // إضافة الترجمات للأقاليم
        const districtsWithTranslations = response.data.data.districts.map(district => ({
          ...district,
          translatedName: getDistrictTranslation(district.name, currentLanguage)
        }));
        setDistricts(districtsWithTranslations || []);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  // Fetch institutions by district
  const fetchInstitutionsByDistrict = async (districtId) => {
    if (!districtId) {
      setInstitutions([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/districts/${districtId}/institutions?page=1&limit=100`);
      if (response.data.success) {
        // إضافة الترجمات للمؤسسات
        const institutionsWithTranslations = response.data.data.institutions.map(institution => ({
          ...institution,
          translatedName: getInstitutionTranslation(institution.name, currentLanguage)
        }));
        setInstitutions(institutionsWithTranslations || []);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
    }
  };

  // Fetch departments by institution
  const fetchDepartmentsByInstitution = async (institutionId) => {
    if (!institutionId) {
      setDepartments([]);
      return;
    }
    
    try {
      const response = await api.get(`/organization/institutions/${institutionId}/departments?page=1&limit=100`);
      if (response.data.success) {
        // إضافة الترجمات للأقسام
        const departmentsWithTranslations = response.data.data.departments.map(department => ({
          ...department,
          translatedName: getDepartmentTranslation(department.name, currentLanguage)
        }));
        setDepartments(departmentsWithTranslations || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  // Handle scope changes
  const handleScopeChange = (field, value) => {
    setNewCompetition(prev => ({
      ...prev,
      scope: {
        ...prev.scope,
        [field]: value || null
      }
    }));

    // Reset dependent fields
    if (field === 'countryId') {
      setNewCompetition(prev => ({
        ...prev,
        scope: {
          countryId: value || null,
          regionId: null,
          academyId: null,
          districtId: null,
          institutionId: null,
          departmentId: null
        }
      }));
      setRegions([]);
      setAcademies([]);
      setDistricts([]);
      setInstitutions([]);
      setDepartments([]);
      if (value) fetchRegionsByCountry(value);
    } else if (field === 'regionId') {
      setNewCompetition(prev => ({
        ...prev,
        scope: {
          ...prev.scope,
          regionId: value || null,
          academyId: null,
          districtId: null,
          institutionId: null,
          departmentId: null
        }
      }));
      setAcademies([]);
      setDistricts([]);
      setInstitutions([]);
      setDepartments([]);
      if (value) fetchAcademiesByRegion(value);
    } else if (field === 'academyId') {
      setNewCompetition(prev => ({
        ...prev,
        scope: {
          ...prev.scope,
          academyId: value || null,
          districtId: null,
          institutionId: null,
          departmentId: null
        }
      }));
      setDistricts([]);
      setInstitutions([]);
      setDepartments([]);
      if (value) fetchDistrictsByAcademy(value);
    } else if (field === 'districtId') {
      setNewCompetition(prev => ({
        ...prev,
        scope: {
          ...prev.scope,
          districtId: value || null,
          institutionId: null,
          departmentId: null
        }
      }));
      setInstitutions([]);
      setDepartments([]);
      if (value) fetchInstitutionsByDistrict(value);
    } else if (field === 'institutionId') {
      setNewCompetition(prev => ({
        ...prev,
        scope: {
          ...prev.scope,
          institutionId: value || null,
          departmentId: null
        }
      }));
      setDepartments([]);
      if (value) fetchDepartmentsByInstitution(value);
    }
  };

  // حل أسماء الهيكل إلى معرفات وتحميل القوائم المتسلسلة (عندما يعيد الـ API أسماء فقط) لنطاق المسابقة
  const resolveCompScopeNamesToIdsAndLoadLists = async (scopeNames, scopeSetter) => {
    if (!scopeNames) return null;
    const setScope = typeof scopeSetter === 'function' ? scopeSetter : null;
    const matchName = (item, name, translated) => !name ? false : (item?.name === name || translated === name);
    let resolved = { countryId: '', regionId: '', academyId: '', districtId: '', institutionId: '', departmentId: '' };
    try {
      const countriesRes = await api.get('/organization/countries?page=1&limit=100').catch(() => ({}));
      const countries = countriesRes?.data?.data?.countries || [];
      const countriesWithTranslations = (countries || []).map(c => ({ ...c, translatedName: getCountryTranslation(c?.name, currentLanguage) }));
      setCountries(countriesWithTranslations);
      const countryName = scopeNames.countryName;
      const country = countryName ? countries.find((c) => matchName(c, countryName, getCountryTranslation(c?.name, currentLanguage))) : null;
      if (country) {
        resolved.countryId = country.id;
        if (setScope) setScope((prev) => ({ ...prev, countryId: country.id }));
        const regionsRes = await api.get(`/organization/countries/${country.id}/regions?page=1&limit=100`).catch(() => ({}));
        const regions = regionsRes?.data?.data?.regions || [];
        const regionsWithTranslations = (regions || []).map(r => ({ ...r, translatedName: getRegionTranslation(r?.name, currentLanguage) }));
        setRegions(regionsWithTranslations);
        const regionName = scopeNames.regionName;
        const region = regionName ? regions.find((r) => matchName(r, regionName, getRegionTranslation(r?.name, currentLanguage))) : null;
        if (region) {
          resolved.regionId = region.id;
          if (setScope) setScope((prev) => ({ ...prev, regionId: region.id }));
          const academiesRes = await api.get(`/organization/regions/${region.id}/academies?page=1&limit=100`).catch(() => ({}));
          const academies = academiesRes?.data?.data?.academies || [];
          const academiesWithTranslations = (academies || []).map(a => ({ ...a, translatedName: getAcademyTranslation(a?.name, currentLanguage) }));
          setAcademies(academiesWithTranslations);
          const academyName = scopeNames.academyName;
          const academy = academyName ? academies.find((a) => matchName(a, academyName, getAcademyTranslation(a?.name, currentLanguage))) : null;
          if (academy) {
            resolved.academyId = academy.id;
            if (setScope) setScope((prev) => ({ ...prev, academyId: academy.id }));
            const districtsRes = await api.get(`/organization/academies/${academy.id}/districts?page=1&limit=100`).catch(() => ({}));
            const districts = districtsRes?.data?.data?.districts || [];
            const districtsWithTranslations = (districts || []).map(d => ({ ...d, translatedName: getDistrictTranslation(d?.name, currentLanguage) }));
            setDistricts(districtsWithTranslations);
            const districtName = scopeNames.districtName;
            const district = districtName ? districts.find((d) => matchName(d, districtName, getDistrictTranslation(d?.name, currentLanguage))) : null;
            if (district) {
              resolved.districtId = district.id;
              if (setScope) setScope((prev) => ({ ...prev, districtId: district.id }));
              const institutionsRes = await api.get(`/organization/districts/${district.id}/institutions?page=1&limit=100`).catch(() => ({}));
              const institutions = institutionsRes?.data?.data?.institutions || [];
              const institutionsWithTranslations = (institutions || []).map(i => ({ ...i, translatedName: getInstitutionTranslation(i?.name, currentLanguage) }));
              setInstitutions(institutionsWithTranslations);
              const institutionName = scopeNames.institutionName;
              const institution = institutionName ? institutions.find((i) => matchName(i, institutionName, getInstitutionTranslation(i?.name, currentLanguage))) : null;
              if (institution) {
                resolved.institutionId = institution.id;
                if (setScope) setScope((prev) => ({ ...prev, institutionId: institution.id }));
                const deptsRes = await api.get(`/organization/institutions/${institution.id}/departments?page=1&limit=100`).catch(() => ({}));
                const depts = deptsRes?.data?.data?.departments || [];
                const deptsWithTranslations = (depts || []).map(d => ({ ...d, translatedName: getDepartmentTranslation(d?.name, currentLanguage) }));
                setDepartments(deptsWithTranslations);
                const departmentName = scopeNames.departmentName;
                const department = departmentName ? depts.find((d) => matchName(d, departmentName, getDepartmentTranslation(d?.name, currentLanguage))) : null;
                if (department) {
                  resolved.departmentId = department.id;
                  if (setScope) setScope((prev) => ({ ...prev, departmentId: department.id }));
                }
              }
            }
          }
        }
      }
      return resolved;
    } catch (e) {
      console.error('resolveCompScopeNamesToIdsAndLoadLists', e);
      return null;
    }
  };

  // Initial load - تحميل البيانات التنظيمية أولاً
  useEffect(() => {
    loadOrganizationalLookupMaps();
  }, []);

  // جلب المسابقات بعد تحميل البيانات التنظيمية
  useEffect(() => {
    if (Object.keys(orgLookupMaps.countries).length > 0) {
      fetchCompetitions();
      fetchStatistics();
    }
  }, [orgLookupMaps]);

  // Update statistics when competitions change
  useEffect(() => {
    if (competitions.length > 0) {
      const calculatedStats = {
        totalCompetitions: competitions.length,
        activeCompetitions: competitions.filter(c => c.status === 'active').length,
        upcomingCompetitions: competitions.filter(c => c.status === 'upcoming' || c.status === 'draft').length,
        completedCompetitions: competitions.filter(c => c.status === 'completed').length,
        totalParticipants: 0,
        averageParticipants: 0
      };
      setStats(calculatedStats);
    }
  }, [competitions]);

  // إعادة تحميل البيانات عند تغيير اللغة لتحديث الترجمات
  useEffect(() => {
    if (currentLanguage && Object.keys(orgLookupMaps.countries).length > 0) {
      loadOrganizationalLookupMaps();
    }
  }, [currentLanguage]);

  const handlePageChange = (page) => {
    fetchCompetitions(page, limit);
  };

  // Open add modal
  const fetchLearningLevels = async () => {
    setLearningLevelsLoading(true);
    try {
      const response = await api.get('/organization/learning-levels?page=1&limit=100');
      if (response.data.success) {
        setLearningLevels(response.data.data.learningLevels || []);
      }
    } catch (error) {
      console.error('Error fetching learning levels:', error);
    } finally {
      setLearningLevelsLoading(false);
    }
  };

  const openAddModal = () => {
    setCompScopeFromCurrentUser(null);
    setCompScopeNamesFromCurrentUser(null);
    setNewCompetition({
      name: '',
      description: '',
      totalRounds: 5,
      flashAnzanMode: 'shared',
      targetLevelId: '',
      scope: {
        countryId: '',
        regionId: '',
        academyId: '',
        districtId: '',
        institutionId: '',
        departmentId: ''
      },
      schedule: {
        registrationStart: '',
        registrationEnd: '',
        competitionStart: '',
        competitionEnd: ''
      },
      settings: {
        maxParticipants: 500,
        allowLateRegistration: false,
        requireApproval: true,
        maxParticipantsPerDistrict: 50,
        maxParticipantsPerAcademy: 100,
        maxParticipantsPerRegion: 200,
        maxParticipantsPerCountry: 500
      }
    });
    setFormErrors({});
    setShowAddModal(true);
    fetchOrganizationalData();
    fetchLearningLevels();
    authService.getCurrentUser()
      .then(async (me) => {
        const access = me?.organizationalAccess?.[0];
        const scope = normalizeOrgScope(access);
        const scopeNames = normalizeOrgScopeNames(access);
        setCompScopeFromCurrentUser(scope);
        setCompScopeNamesFromCurrentUser(scopeNames);
        if (scope) {
          setNewCompetition(prev => ({
            ...prev,
            scope: {
              countryId: scope.countryId || '',
              regionId: scope.regionId || '',
              academyId: scope.academyId || '',
              districtId: scope.districtId || '',
              institutionId: scope.institutionId || '',
              departmentId: scope.departmentId || ''
            }
          }));
          if (scope.countryId) await fetchRegionsByCountry(scope.countryId);
          if (scope.regionId) await fetchAcademiesByRegion(scope.regionId);
          if (scope.academyId) await fetchDistrictsByAcademy(scope.academyId);
          if (scope.districtId) await fetchInstitutionsByDistrict(scope.districtId);
          if (scope.institutionId) await fetchDepartmentsByInstitution(scope.institutionId);
        } else if (scopeNames && (scopeNames.countryName || scopeNames.regionName || scopeNames.academyName || scopeNames.districtName || scopeNames.institutionName || scopeNames.departmentName)) {
          const setScopeUpdater = (updater) => setNewCompetition(prev => ({ ...prev, scope: typeof updater === 'function' ? updater(prev.scope) : { ...prev.scope, ...updater } }));
          const resolved = await resolveCompScopeNamesToIdsAndLoadLists(scopeNames, setScopeUpdater);
          if (resolved) setCompScopeFromCurrentUser(resolved);
        }
      })
      .catch((err) => console.error('getCurrentUser for competition scope', err));
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setFormErrors({});
    setCompScopeFromCurrentUser(null);
    setCompScopeNamesFromCurrentUser(null);
    setRegions([]);
    setAcademies([]);
    setDistricts([]);
    setInstitutions([]);
    setDepartments([]);
  };

  // Handle add competition
  const handleAddCompetition = async (e) => {
    e.preventDefault();
    
    // Validation
    const errors = {};
    if (!newCompetition.name.trim()) {
      errors.name = t('competitions.titleRequired');
    }
    if (!newCompetition.schedule.registrationStart) {
      errors.registrationStart = t('competitions.registrationStartRequired');
    }
    if (!newCompetition.schedule.registrationEnd) {
      errors.registrationEnd = t('competitions.registrationEndRequired');
    }
    if (!newCompetition.schedule.competitionStart) {
      errors.competitionStart = t('competitions.competitionStartRequired');
    }
    if (!newCompetition.schedule.competitionEnd) {
      errors.competitionEnd = t('competitions.competitionEndRequired');
    }
    const effectiveCountryId = compScopeFromCurrentUser?.countryId ?? newCompetition.scope.countryId;
    if (!effectiveCountryId) {
      errors.countryId = t('competitions.countryRequired');
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      // Prepare data for API
      const requestData = {
        name: newCompetition.name.trim(),
        description: newCompetition.description.trim(),
        totalRounds: parseInt(newCompetition.totalRounds),
        flashAnzanMode: newCompetition.flashAnzanMode,
        ...(newCompetition.targetLevelId ? { targetLevelId: parseInt(newCompetition.targetLevelId) } : {}),
        scope: {
          countryId: parseInt(compScopeFromCurrentUser?.countryId ?? newCompetition.scope.countryId) || null,
          regionId: parseInt(compScopeFromCurrentUser?.regionId ?? newCompetition.scope.regionId) || null,
          academyId: parseInt(compScopeFromCurrentUser?.academyId ?? newCompetition.scope.academyId) || null,
          districtId: parseInt(compScopeFromCurrentUser?.districtId ?? newCompetition.scope.districtId) || null,
          institutionId: parseInt(compScopeFromCurrentUser?.institutionId ?? newCompetition.scope.institutionId) || null,
          departmentId: parseInt(compScopeFromCurrentUser?.departmentId ?? newCompetition.scope.departmentId) || null
        },
        schedule: {
          registrationStart: new Date(newCompetition.schedule.registrationStart).toISOString(),
          registrationEnd: new Date(newCompetition.schedule.registrationEnd).toISOString(),
          competitionStart: new Date(newCompetition.schedule.competitionStart).toISOString(),
          competitionEnd: new Date(newCompetition.schedule.competitionEnd).toISOString()
        },
        settings: {
          maxParticipants: parseInt(newCompetition.settings.maxParticipants),
          allowLateRegistration: newCompetition.settings.allowLateRegistration,
          requireApproval: newCompetition.settings.requireApproval,
          maxParticipantsPerDistrict: parseInt(newCompetition.settings.maxParticipantsPerDistrict),
          maxParticipantsPerAcademy: parseInt(newCompetition.settings.maxParticipantsPerAcademy),
          maxParticipantsPerRegion: parseInt(newCompetition.settings.maxParticipantsPerRegion),
          maxParticipantsPerCountry: parseInt(newCompetition.settings.maxParticipantsPerCountry)
        }
      };

      console.log('Creating competition with data:', requestData);
      
      const response = await api.post('/competitions/create', requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('competitions.addSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        closeAddModal();
        fetchCompetitions();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error adding competition:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || t('competitions.addError'), {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsAdding(false);
    }
  };

  // Delete competition
  // Open status modal
  const openStatusModal = (competition) => {
    setSelectedCompetition(competition);
    setNewStatus(competition.status);
    setShowStatusModal(true);
  };

  // Close status modal
  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedCompetition(null);
    setNewStatus('');
  };

  // Update competition status
  const updateCompetitionStatus = async () => {
    if (!selectedCompetition || !newStatus) {
      toast.error(t('competitions.selectStatusFirst'), { duration: 3000, position: 'top-center' });
      return;
    }

    setIsUpdatingStatus(true);
    try {
      console.log('🔄 Updating status for:', selectedCompetition.competitionId);
      console.log('📤 New status:', newStatus);
      console.log('🌐 API endpoint:', `/competitions/${selectedCompetition.competitionId}/status`);
      
      const response = await api.patch(`/competitions/${selectedCompetition.competitionId}/status`, {
        status: newStatus
      });
      
      console.log('✅ Response:', response.data);
      
      if (response.data.success) {
        toast.success(t('competitions.statusUpdateSuccess'), {
          duration: 3000,
          position: 'top-center'
        });
        closeStatusModal();
        fetchCompetitions(currentPage, limit);
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error updating competition status:', error);
      toast.error(error.response?.data?.message || t('competitions.statusUpdateError'), {
        duration: 3000,
        position: 'top-center'
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const deleteCompetition = async (competitionId, competitionTitle) => {
    if (window.confirm(t('competitions.confirmDelete', { title: competitionTitle }))) {
      try {
        const response = await api.delete(`/competitions/${competitionId}`);
        
        if (response.data.success) {
          toast.success(response.data.message || t('competitions.deleteSuccess'), {
            duration: 3000,
            position: 'top-center'
          });
          fetchCompetitions(currentPage, limit);
          fetchStatistics();
        }
      } catch (error) {
        console.error('Error deleting competition:', error);
        toast.error(error.response?.data?.message || t('competitions.deleteError'), {
          duration: 3000,
          position: 'top-center'
        });
      }
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: ClockIcon, text: t('competitions.draft') },
      upcoming: { color: 'bg-blue-100 text-blue-800', icon: ClockIcon, text: t('competitions.upcoming') },
      active: { color: 'bg-green-100 text-green-800', icon: PlayCircleIcon, text: t('competitions.active') },
      completed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircleIcon, text: t('competitions.completed') },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircleIcon, text: t('competitions.cancelled') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

  return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className={`h-3 w-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
        {config.text}
      </span>
    );
  };

  // Format date with time (24-hour format)
  const formatDate = (dateString) => {
    if (!dateString) return t('common.notAvailable');
    const date = new Date(dateString);
    return date.toLocaleString(
      currentLanguage === 'ar' ? 'ar-SA' : (currentLanguage === 'en' ? 'en-US' : 'fr-FR'),
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }
    );
  };

  return (
    <div className={`p-6 ${isRTL ? 'font-arabic' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <TrophyIcon className={`h-8 w-8 text-primary-600 ${isRTL ? 'ml-3' : 'mr-3'}`} />
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('competitions.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('competitions.subtitle')}
        </p>
      </div>
          </div>
          <button 
            onClick={openAddModal}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
          >
            <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('competitions.addCompetition')}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
      <div>
              <p className="text-sm text-gray-600">{t('competitions.total')}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCompetitions}</p>
            </div>
            <TrophyIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('competitions.active')}</p>
              <p className="text-2xl font-bold text-green-600">{stats.activeCompetitions}</p>
            </div>
            <PlayCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('competitions.upcoming')}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.upcomingCompetitions}</p>
            </div>
            <ClockIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('competitions.completed')}</p>
              <p className="text-2xl font-bold text-gray-600">{stats.completedCompetitions}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-gray-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('competitions.totalParticipants')}</p>
              <p className="text-2xl font-bold text-indigo-600">{stats.totalParticipants}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
      <div>
              <p className="text-sm text-gray-600">{t('competitions.avgParticipants')}</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.averageParticipants}</p>
            </div>
            <TrendingUpIcon className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Competitions List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className={`${isRTL ? 'mr-3' : 'ml-3'} text-gray-600`}>{t('common.loading')}</span>
          </div>
        ) : competitions.length === 0 ? (
          <div className="text-center py-12">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('competitions.noCompetitions')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('competitions.noCompetitionsDesc')}</p>
            <div className="mt-6">
              <button
                onClick={openAddModal}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('competitions.addCompetition')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('competitions.competitionTitle')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('competitions.scope')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('competitions.schedule')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('competitions.rounds')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('competitions.status')}
                    </th>
                    <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('competitions.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {competitions.map((competition) => (
                    <tr key={competition.id} className="hover:bg-gray-50">
                      <td className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <TrophyIcon className="h-5 w-5 text-primary-600" />
                            </div>
                          </div>
                          <div className={`${isRTL ? 'mr-4' : 'ml-4'}`}>
                            <div className="text-sm font-medium text-gray-900">
                              {competition.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {competition.competitionId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="text-sm text-gray-900">
                          {competition.scopeInfo?.deepestLevel ? (
                            <div className="flex items-center">
                              {competition.scopeInfo.deepestLevelIcon && 
                                React.createElement(competition.scopeInfo.deepestLevelIcon, {
                                  className: `h-4 w-4 text-gray-400 ${isRTL ? 'ml-2' : 'mr-2'}`
                                })
                              }
                              <span>{competition.scopeInfo.deepestLevel}</span>
                            </div>
                          ) : (
                            <span className="text-gray-500">{t('common.notAvailable')}</span>
                          )}
                        </div>
                      </td>
                      <td className={`px-6 py-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs font-medium text-gray-600">Start: </span>
                            <span className="text-sm text-gray-900">
                              {formatDate(competition.schedule?.competitionStart)}
                            </span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-600">End: </span>
                            <span className="text-sm text-gray-900">
                              {formatDate(competition.schedule?.competitionEnd)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {competition.totalRounds}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                        {getStatusBadge(competition.status)}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                        <div className={`flex items-center ${isRTL ? 'justify-end space-x-reverse space-x-2' : 'justify-start space-x-2'}`}>
                          <button 
                            onClick={() => {
                              const userRole = user?.role?.toLowerCase().replace('_', '-') || 'super-admin';
                              navigate(`/dashboard/competitions/${competition.competitionId}/students`);
                            }}
                            className="text-green-600 hover:text-green-900 p-1" 
                            title={t('competitions.manageStudents')}
                          >
                            <UsersIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => {
                              const userRole = user?.role?.toLowerCase().replace('_', '-') || 'super-admin';
                              navigate(`/dashboard/competitions/${competition.competitionId}/participants`);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1" 
                            title={t('competitions.viewParticipants')}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => {
                              const userRole = user?.role?.toLowerCase().replace('_', '-') || 'super-admin';
                              navigate(`/dashboard/competitions/${competition.competitionId}/rounds`);
                            }}
                            className="text-orange-600 hover:text-orange-900 p-1" 
                            title={t('competitions.manageRounds')}
                          >
                            <LayersIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => openStatusModal(competition)}
                            className="text-indigo-600 hover:text-indigo-900 p-1" 
                            title={t('competitions.updateStatus')}
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
      </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`bg-white px-4 py-3 flex items-center ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} border-t border-gray-200`}>
                <div>
                  <p className="text-sm text-gray-700">
                    {t('competitions.showing')} <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> {t('competitions.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, total)}</span> {t('competitions.of')}{' '}
                    <span className="font-medium">{total}</span> {t('competitions.results')}
        </p>
      </div>
                <div>
                  <nav className={`relative z-0 inline-flex rounded-md shadow-sm ${isRTL ? 'space-x-reverse -space-x-px' : '-space-x-px'}`}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &lt;
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === index + 1
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      &gt;
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Competition Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className={`flex items-center sticky top-0 bg-white z-10 ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} p-6 border-b border-gray-200`}>
              <h3 className={`text-xl font-medium text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('competitions.addCompetition')}
              </h3>
              <button
                onClick={closeAddModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleAddCompetition} className="p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('competitions.basicInfo')}</h4>
                  
                  {/* Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('competitions.competitionTitle')} *
                    </label>
                    <input
                      type="text"
                      value={newCompetition.name}
                      onChange={(e) => setNewCompetition(prev => ({ ...prev, name: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder={t('competitions.titlePlaceholder')}
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('competitions.description')}
                    </label>
                    <textarea
                      value={newCompetition.description}
                      onChange={(e) => setNewCompetition(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('competitions.descriptionPlaceholder')}
                    />
                  </div>

                  {/* Total Rounds and Flash Anzan Mode */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.totalRounds')}
                      </label>
                      <input
                        type="number"
                        value={newCompetition.totalRounds}
                        onChange={(e) => setNewCompetition(prev => ({ ...prev, totalRounds: e.target.value }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.flashAnzanMode')}
                      </label>
                      <select
                        value={newCompetition.flashAnzanMode}
                        onChange={(e) => setNewCompetition(prev => ({ ...prev, flashAnzanMode: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="shared">{t('competitions.shared')}</option>
                        <option value="individual">{t('competitions.individual')}</option>
                      </select>
                    </div>
                  </div>

                  {/* Target Level */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('competitions.targetLevel')}
                    </label>
                    <select
                      value={newCompetition.targetLevelId}
                      onChange={(e) => setNewCompetition(prev => ({ ...prev, targetLevelId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={learningLevelsLoading}
                    >
                      <option value="">{learningLevelsLoading ? t('users.loadingLevels') : t('competitions.selectTargetLevel')}</option>
                      {learningLevels.map((level) => {
                        const key = `learningLevels.names.${level.id}`;
                        const translated = t(key);
                        const label = translated !== key ? translated : (level.name_ar || level.name || `Level ${level.level_number ?? level.id}`);
                        return (
                          <option key={level.id} value={level.id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* Scope - نطاق المسابقة (مقيد بهيكل المستخدم الحالي) */}
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('competitions.competitionScope')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Country */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.country')} *
                        {isCompScopeLevelFixed('countryId') && (
                          <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                            <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                          </span>
                        )}
                      </label>
                      {isCompScopeLevelFixed('countryId') ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                          {getCountryTranslation(compScopeNamesFromCurrentUser?.countryName, currentLanguage) || compScopeNamesFromCurrentUser?.countryName || t('common.notAvailable')}
                        </div>
                      ) : (
                        <select
                          value={newCompetition.scope.countryId}
                          onChange={(e) => handleScopeChange('countryId', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${formErrors.countryId ? 'border-red-300' : 'border-gray-300'}`}
                          disabled={loadingOrgData}
                        >
                          <option value="">{loadingOrgData ? t('users.loadingCountries') : t('users.selectCountry')}</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>{country.translatedName || country.name}</option>
                          ))}
                        </select>
                      )}
                      {formErrors.countryId && <p className="mt-1 text-sm text-red-600">{formErrors.countryId}</p>}
                    </div>

                    {/* Region */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.region')}
                        {isCompScopeLevelFixed('regionId') && (
                          <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                            <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                          </span>
                        )}
                      </label>
                      {isCompScopeLevelFixed('regionId') ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                          {getRegionTranslation(compScopeNamesFromCurrentUser?.regionName, currentLanguage) || compScopeNamesFromCurrentUser?.regionName || t('common.notAvailable')}
                        </div>
                      ) : (
                        <select
                          value={newCompetition.scope.regionId}
                          onChange={(e) => handleScopeChange('regionId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!newCompetition.scope.countryId}
                        >
                          <option value="">{!newCompetition.scope.countryId ? t('users.selectCountryFirst') : t('users.selectRegion')}</option>
                          {regions.map((region) => (
                            <option key={region.id} value={region.id}>{region.translatedName || region.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Academy */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.academy')}
                        {isCompScopeLevelFixed('academyId') && (
                          <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                            <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                          </span>
                        )}
                      </label>
                      {isCompScopeLevelFixed('academyId') ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                          {getAcademyTranslation(compScopeNamesFromCurrentUser?.academyName, currentLanguage) || compScopeNamesFromCurrentUser?.academyName || t('common.notAvailable')}
                        </div>
                      ) : (
                        <select
                          value={newCompetition.scope.academyId}
                          onChange={(e) => handleScopeChange('academyId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!newCompetition.scope.regionId}
                        >
                          <option value="">{!newCompetition.scope.regionId ? t('users.selectRegionFirst') : t('users.selectAcademy')}</option>
                          {academies.map((academy) => (
                            <option key={academy.id} value={academy.id}>{academy.translatedName || academy.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* District */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.district')}
                        {isCompScopeLevelFixed('districtId') && (
                          <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                            <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                          </span>
                        )}
                      </label>
                      {isCompScopeLevelFixed('districtId') ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                          {getDistrictTranslation(compScopeNamesFromCurrentUser?.districtName, currentLanguage) || compScopeNamesFromCurrentUser?.districtName || t('common.notAvailable')}
                        </div>
                      ) : (
                        <select
                          value={newCompetition.scope.districtId}
                          onChange={(e) => handleScopeChange('districtId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!newCompetition.scope.academyId}
                        >
                          <option value="">{!newCompetition.scope.academyId ? t('users.selectAcademyFirst') : t('users.selectDistrict')}</option>
                          {districts.map((district) => (
                            <option key={district.id} value={district.id}>{district.translatedName || district.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Institution */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.institution')}
                        {isCompScopeLevelFixed('institutionId') && (
                          <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                            <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                          </span>
                        )}
                      </label>
                      {isCompScopeLevelFixed('institutionId') ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                          {getInstitutionTranslation(compScopeNamesFromCurrentUser?.institutionName, currentLanguage) || compScopeNamesFromCurrentUser?.institutionName || t('common.notAvailable')}
                        </div>
                      ) : (
                        <select
                          value={newCompetition.scope.institutionId}
                          onChange={(e) => handleScopeChange('institutionId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!newCompetition.scope.districtId}
                        >
                          <option value="">{!newCompetition.scope.districtId ? t('users.selectDistrictFirst') : t('users.selectInstitution')}</option>
                          {institutions.map((institution) => (
                            <option key={institution.id} value={institution.id}>{institution.translatedName || institution.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Department */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('users.department')}
                        {isCompScopeLevelFixed('departmentId') && (
                          <span className={`text-xs text-gray-500 font-normal ${isRTL ? 'mr-1' : 'ml-1'}`} title={t('users.orgLevelLocked')}>
                            <LockIcon className="inline w-3.5 h-3.5 align-middle" /> {t('users.orgLevelLocked')}
                          </span>
                        )}
                      </label>
                      {isCompScopeLevelFixed('departmentId') ? (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed select-none">
                          {getDepartmentTranslation(compScopeNamesFromCurrentUser?.departmentName, currentLanguage) || compScopeNamesFromCurrentUser?.departmentName || t('common.notAvailable')}
                        </div>
                      ) : (
                        <select
                          value={newCompetition.scope.departmentId}
                          onChange={(e) => handleScopeChange('departmentId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!newCompetition.scope.institutionId}
                        >
                          <option value="">{!newCompetition.scope.institutionId ? t('users.selectInstitutionFirst') : t('users.selectDepartment')}</option>
                          {departments.map((department) => (
                            <option key={department.id} value={department.id}>{department.translatedName || department.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div className="border-b border-gray-200 pb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('competitions.schedule')}</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.registrationStart')} *
                      </label>
                      <input
                        type="datetime-local"
                        value={newCompetition.schedule.registrationStart}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          schedule: { ...prev.schedule, registrationStart: e.target.value }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          formErrors.registrationStart ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {formErrors.registrationStart && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.registrationStart}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.registrationEnd')} *
                      </label>
                      <input
                        type="datetime-local"
                        value={newCompetition.schedule.registrationEnd}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          schedule: { ...prev.schedule, registrationEnd: e.target.value }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          formErrors.registrationEnd ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {formErrors.registrationEnd && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.registrationEnd}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.competitionStart')} *
                      </label>
                      <input
                        type="datetime-local"
                        value={newCompetition.schedule.competitionStart}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          schedule: { ...prev.schedule, competitionStart: e.target.value }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          formErrors.competitionStart ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {formErrors.competitionStart && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.competitionStart}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.competitionEnd')} *
                      </label>
                      <input
                        type="datetime-local"
                        value={newCompetition.schedule.competitionEnd}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          schedule: { ...prev.schedule, competitionEnd: e.target.value }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          formErrors.competitionEnd ? 'border-red-300' : 'border-gray-300'
                        }`}
                      />
                      {formErrors.competitionEnd && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.competitionEnd}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{t('competitions.settings')}</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.maxParticipants')}
                      </label>
                      <input
                        type="number"
                        value={newCompetition.settings.maxParticipants}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxParticipants: e.target.value }
                        }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.maxPerCountry')}
                      </label>
                      <input
                        type="number"
                        value={newCompetition.settings.maxParticipantsPerCountry}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxParticipantsPerCountry: e.target.value }
                        }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.maxPerRegion')}
                      </label>
                      <input
                        type="number"
                        value={newCompetition.settings.maxParticipantsPerRegion}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxParticipantsPerRegion: e.target.value }
                        }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.maxPerAcademy')}
                      </label>
                      <input
                        type="number"
                        value={newCompetition.settings.maxParticipantsPerAcademy}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxParticipantsPerAcademy: e.target.value }
                        }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('competitions.maxPerDistrict')}
                      </label>
                      <input
                        type="number"
                        value={newCompetition.settings.maxParticipantsPerDistrict}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxParticipantsPerDistrict: e.target.value }
                        }))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newCompetition.settings.requireApproval}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, requireApproval: e.target.checked }
                        }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-gray-700`}>
                        {t('competitions.requireApproval')}
                      </span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newCompetition.settings.allowLateRegistration}
                        onChange={(e) => setNewCompetition(prev => ({
                          ...prev,
                          settings: { ...prev.settings, allowLateRegistration: e.target.checked }
                        }))}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-gray-700`}>
                        {t('competitions.allowLateRegistration')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'} mt-6 pt-4 border-t border-gray-200`}>
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isAdding}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isAdding}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isAdding ? (
                    <>
                      <div className={`animate-spin rounded-full h-4 w-4 border-b-2 border-white ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                      {t('competitions.adding')}
                    </>
                  ) : (
                    <>
                      <PlusIcon className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t('competitions.addCompetition')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedCompetition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg shadow-xl max-w-md w-full ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t('competitions.updateStatus')}
              </h2>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">{t('competitions.competitionTitle')}:</span> {selectedCompetition.name}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('competitions.currentStatus')}:</span> {getStatusBadge(selectedCompetition.status)}
        </p>
      </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('competitions.newStatus')}
                </label>
                <div className="space-y-3">
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${newStatus === 'active' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      checked={newStatus === 'active'}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500"
                    />
                    <span className={`${isRTL ? 'mr-3' : 'ml-3'} flex items-center gap-2`}>
                      <PlayCircleIcon className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-gray-900">{t('competitions.active')}</span>
                    </span>
                  </label>

                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${newStatus === 'draft' ? 'border-gray-500 bg-gray-50' : 'border-gray-200'}`}>
                    <input
                      type="radio"
                      name="status"
                      value="draft"
                      checked={newStatus === 'draft'}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="h-4 w-4 text-gray-600 focus:ring-gray-500"
                    />
                    <span className={`${isRTL ? 'mr-3' : 'ml-3'} flex items-center gap-2`}>
                      <ClockIcon className="h-5 w-5 text-gray-600" />
                      <span className="font-medium text-gray-900">{t('competitions.draft')}</span>
                    </span>
                  </label>

                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${newStatus === 'completed' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                    <input
                      type="radio"
                      name="status"
                      value="completed"
                      checked={newStatus === 'completed'}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className={`${isRTL ? 'mr-3' : 'ml-3'} flex items-center gap-2`}>
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">{t('competitions.completed')}</span>
                    </span>
                  </label>

                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${newStatus === 'cancelled' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                    <input
                      type="radio"
                      name="status"
                      value="cancelled"
                      checked={newStatus === 'cancelled'}
                      onChange={(e) => setNewStatus(e.target.value)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500"
                    />
                    <span className={`${isRTL ? 'mr-3' : 'ml-3'} flex items-center gap-2`}>
                      <XCircleIcon className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-gray-900">{t('competitions.cancelled')}</span>
                    </span>
                  </label>
                </div>
              </div>

              <div className={`flex ${isRTL ? 'justify-start flex-row-reverse space-x-reverse' : 'justify-end'} space-x-3`}>
                <button
                  type="button"
                  onClick={closeStatusModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={updateCompetitionStatus}
                  disabled={isUpdatingStatus}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {isUpdatingStatus ? t('competitions.updating') : t('common.update')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionManagement;
